import React from 'react';
import PropTypes from 'prop-types';

import styles from './Select.css';
import { KeyboardHelper, keyCodes } from '../../helpers/KeyboardHelper';
import { UserAgentHelper } from '../../helpers/UserAgentHelper';

const IValue = PropTypes.oneOfType([
  PropTypes.node,
  PropTypes.func,
]);

const ILabel = PropTypes.oneOfType([
  PropTypes.node,
  PropTypes.element,
]);

const IPropTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  dropdownClassName: PropTypes.string,
  noIndicator: PropTypes.bool,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: IValue,
    label: ILabel,
  })).isRequired,
  optionClassName: PropTypes.string,
  rightAligned: PropTypes.bool,
  selectedClassName: PropTypes.string,
  value: IValue.isRequired,
};

const defaultProps = {
  className: '',
  disabled: false,
  dropdownClassName: '',
  noIndicator: false,
  onChange: () => { },
  optionClassName: '',
  rightAligned: false,
  selectedClassName: '',
};

class Select extends React.Component {
  static noScroll() {
    const { scrollX, scrollY } = window;
    window.scrollTo(scrollX, scrollY);
  }
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      selectedOption: 0,
    };
    this.container = null;
    this.references = this.props.options.map(() => React.createRef());
    this._generateElementCallback = this._generateElementCallback.bind(this);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
    this._onSelectedElementClick = this._onSelectedElementClick.bind(this);
    this._toggleDropdown = this._toggleDropdown.bind(this);
    this._handleKeyPress = this._handleKeyPress.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', this._handleDocumentClick, false);
    document.addEventListener('touchend', this._handleDocumentClick, false);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this._handleDocumentClick, false);
    document.removeEventListener('touchend', this._handleDocumentClick, false);
  }

  _handleDocumentClick(event) {
    if (this.container && !this.container.contains(event.target)) {
      if (this.state.isOpen) {
        this._toggleDropdown();
      }
    }
  }

  _toggleDropdown() {
    if (this.state.isOpen) {
      document.removeEventListener('keydown', this._handleKeyPress);
      // On IE 11 or bellow scrollIntoView scroll into window of browser too, so
      // blocking scroll on window, scrollIntoView scroll only on select options.
      if (UserAgentHelper.isIE11OrBellow) {
        window.removeEventListener('scroll', Select.noScroll);
      }
    } else {
      document.addEventListener('keydown', this._handleKeyPress);
      if (UserAgentHelper.isIE11OrBellow) {
        window.addEventListener('scroll', Select.noScroll);
      }
    }
    if (!this.props.disabled) {
      this.setState(prevState => ({
        isOpen: !prevState.isOpen,
        selectedOption: 0,
      }));
    }
  }

  _generateElementCallback(value) {
    return (event) => {
      event.preventDefault();
      if (!this.props.disabled) {
        this.props.onChange(value, true);
        this._toggleDropdown();
      }
    };
  }

  _onSelectedElementClick(event) {
    event.preventDefault();
    this._toggleDropdown();
  }

  _getRefByIndex(index) {
    if (this.references.length < index + 1) {
      this.references.length = index;
      for (let i = 0; i <= index; i += 1) {
        this.references[i] = this.references[i] || React.createRef();
      }
    }
    return this.references[index];
  }

  _scrollToRef(selectedOption) {
    if (selectedOption < this.props.options.length) {
      this.setState({ selectedOption: selectedOption + 1 });
      // NOTE: sometimes the select crashes because the reference can't be found.
      // The reason of the crash could not be found in a reasonable time, so this
      // was done to avoid the crash.
      if (!this.references[selectedOption] || !this.references[selectedOption].current) {
        return;
      }
      const currentReference = this.references[selectedOption].current;
      const offset = currentReference.offsetTop;
      const referenceParent = currentReference.parentNode;
      referenceParent.scrollTop = offset;
    } else {
      this.setState({ selectedOption: this.props.options.length });
    }
  }

  _handleKeyPress(event) {
    if (event.keyCode === keyCodes.esc) {
      this._toggleDropdown();
    } else if (event.keyCode === keyCodes.enter && this.state.selectedOption > 0) {
      this.props.onChange(this.props.options[this.state.selectedOption - 1].value, true);
      this._toggleDropdown();
    } else if (event.keyCode === keyCodes.arrowDown) {
      let selectedOption = -1;
      if (this.state.selectedOption < this.props.options.length) {
        selectedOption = this.state.selectedOption + 1;
        this.setState({ selectedOption });
      } else {
        selectedOption = 1;
        this.setState({ selectedOption });
      }
      if (this.props.options[this.state.selectedOption - 1]) {
        this.props.onChange(this.props.options[this.state.selectedOption - 1].value, false);
      }
      this._scrollToRef(selectedOption - 1);
    } else if (event.keyCode === keyCodes.arrowUp) {
      let selectedOption = -1;
      if (this.state.selectedOption > 1) {
        selectedOption = this.state.selectedOption - 1;
        this.setState({ selectedOption });
      } else {
        selectedOption = this.props.options.length;
        this.setState({ selectedOption });
      }
      if (this.props.options[this.state.selectedOption - 1]) {
        this.props.onChange(this.props.options[this.state.selectedOption - 1].value, false);
      }
      this._scrollToRef(this.state.selectedOption - 1);
    } else if (KeyboardHelper.getCharFromEventListener(event.keyCode)) {
      const letter = KeyboardHelper.getCharFromEventListener(event.keyCode);
      const selectedOption = this.props.options.findIndex(element =>
        element.label.toString().charAt(0).toUpperCase() === letter.toUpperCase());
      if (selectedOption >= 0) {
        this.props.onChange(this.props.options[selectedOption].value, false);
        this._scrollToRef(selectedOption);
      }
    }
    event.preventDefault();
  }

  render() {
    const rightAlignedClass = this.props.rightAligned ? styles.right : styles.left;
    return (
      <div
        className={[styles.container, this.props.className, this.props.disabled ? styles.disabled : ''].join(' ')}
        ref={(container) => { this.container = container; }}
      >
        <button
          className={[styles.value, this.props.selectedClassName].join(' ')}
          onClick={this._onSelectedElementClick}
        >
          {this.props.value}
          {
            !this.props.noIndicator &&
            <div className={styles.arrow}>
              ▾
            </div>
          }
        </button>
        {
          this.state.isOpen &&
          !this.props.disabled &&
          <div
            className={[styles.dropdown, this.props.dropdownClassName, rightAlignedClass].join(' ')}
          >
            {
              this.props.options.map((option, index) => (
                <button
                  className={[
                    styles.element,
                    this.props.optionClassName,
                    (this.state.selectedOption - 1 === index)
                      ? styles.selectedElement
                      : null,
                  ].join(' ')}
                  ref={this._getRefByIndex(index)}
                  key={option.value}
                  onClick={this._generateElementCallback(option.value)}
                >
                  {option.label}
                </button>
              ))
            }
          </div>
        }
      </div>
    );
  }
}

Select.propTypes = IPropTypes;
Select.defaultProps = defaultProps;

export { Select };



// WEBPACK FOOTER //
// ./src/common/select/Select.jsx